import axios from "axios";
import { imageFile } from "@/lib/validate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Backend media is served under /api/images/<id>; nginx puts it on one origin in
// production, but in dev the API is on another port. Anything else — absolute
// URLs, data:, and frontend static assets like /themes/x.jpg — passes through.
export const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

let accessToken = "";
export const setAccessToken = (t) => { accessToken = t || ""; };
export const getAccessToken = () => accessToken;

// Set by AuthContext so a dropped session clears the UI instead of looping.
let onAuthLost = () => {};
export const setOnAuthLost = (fn) => { onAuthLost = fn || (() => {}); };

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
    if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
    return cfg;
});

// The access token expires after ACCESS_MINUTES. Without this, every call after
// that point failed until the user hard-reloaded the tab.
let refreshing = null;

// Single-flight, and shared with AuthContext's bootstrap. The refresh token
// rotates on redemption, so two calls racing would burn each other's cookie —
// which the server used to read as a stolen token and answer by ending every
// session the user had.
export const refreshSession = () => {
    refreshing = refreshing || client.post("/auth/refresh").finally(() => { refreshing = null; });
    return refreshing;
};

client.interceptors.response.use(
    (r) => r,
    async (error) => {
        const cfg = error?.config;
        const status = error?.response?.status;
        const isRefreshCall = cfg?.url?.includes("/auth/refresh");
        if (status !== 401 || !cfg || cfg._retried || isRefreshCall) {
            return Promise.reject(error);
        }
        cfg._retried = true;
        try {
            const { data } = await refreshSession();
            setAccessToken(data.access_token);
            cfg.headers = { ...cfg.headers, Authorization: `Bearer ${data.access_token}` };
            return client(cfg);
        } catch (e) {
            setAccessToken("");
            onAuthLost();
            return Promise.reject(error);
        }
    }
);

export default client;

// Guarded here rather than at each picker: this is the only path a chosen file
// takes into memory, and reading an 80MB video as base64 hangs the tab long
// before the server gets a chance to refuse it.
export const fileToDataURL = (file) => new Promise((resolve, reject) => {
    const bad = imageFile(file);
    if (bad) return reject(new Error(bad));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("That file could not be read. Please try another."));
    r.readAsDataURL(file);
});
